import React, { useState } from 'react';
import { 
  UserPlus, 
  Edit, 
  Trash2, 
  Lock, 
  Unlock, 
  Eye, 
  Search, 
  Plus, 
  Mail,
  Calendar,
  Shield,
  Copy,
  ArrowUpDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

const InternalAccounts = () => {
  const { toast } = useToast();
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [hoveredField, setHoveredField] = useState(null);

  // Copy function
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Đã sao chép",
        description: "Đã sao chép vào clipboard",
      });
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast({
        title: "Lỗi",
        description: "Không thể sao chép",
        variant: "destructive"
      });
    }
  };

  const [users] = useState([
    {
      id: 'NV001',
      name: 'Lê Văn C',
      email: 'levanc@company.com',
      phone: '0987654321',
      role: 'admin',
      status: 'active',
      createdAt: '2024-11-01T09:00:00+07:00',
      lastLogin: '2025-01-12T08:30:00+07:00',
      department: 'Quản lý'
    },
    {
      id: 'NV002',
      name: 'Phạm Thị D',
      email: 'phamthid@company.com',
      phone: '0123456789',
      role: 'manager',
      status: 'active',
      createdAt: '2024-10-15T16:00:00+07:00',
      lastLogin: '2025-01-11T17:45:00+07:00',
      department: 'Vận hành'
    },
    {
      id: 'NV003',
      name: 'Nguyễn Văn E',
      email: 'nguyenvane@company.com',
      phone: '0345678901',
      role: 'staff',
      status: 'locked',
      createdAt: '2024-09-20T14:20:00+07:00',
      lastLogin: '2025-01-05T10:15:00+07:00',
      department: 'Kho bãi'
    }
  ]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'locked':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getRoleText = (role) => {
    switch (role) {
      case 'admin': return 'Quản trị viên';
      case 'manager': return 'Quản lý';
      case 'staff': return 'Nhân viên';
      default: return role;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'manager': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'staff': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.department.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Tài khoản nội bộ</h1>
          <p className="text-muted-foreground">Quản lý tài khoản nhân viên và người dùng hệ thống</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Thêm tài khoản
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Thêm tài khoản nội bộ mới</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Thông tin cơ bản */}
              <div className="border-b pb-4">
                <h3 className="font-medium mb-3">Thông tin cơ bản</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="userName">Họ và tên *</Label>
                    <Input placeholder="Nhập họ và tên" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="userEmail">Email *</Label>
                    <Input type="email" placeholder="Nhập email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="userPhone">Số điện thoại</Label>
                    <Input placeholder="Nhập số điện thoại" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="department">Phòng ban</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Chọn phòng ban" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="management">Quản lý</SelectItem>
                        <SelectItem value="operations">Vận hành</SelectItem>
                        <SelectItem value="warehouse">Kho bãi</SelectItem>
                        <SelectItem value="customer_service">Chăm sóc khách hàng</SelectItem>
                        <SelectItem value="it">Công nghệ thông tin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Phân quyền */}
              <div className="border-b pb-4">
                <h3 className="font-medium mb-3">Phân quyền</h3>
                <div className="space-y-2">
                  <Label htmlFor="role">Vai trò *</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn vai trò" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Quản trị viên</SelectItem>
                      <SelectItem value="manager">Quản lý</SelectItem>
                      <SelectItem value="staff">Nhân viên</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tài khoản */}
              <div className="border-b pb-4">
                <h3 className="font-medium mb-3">Thông tin tài khoản</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="username">Tên đăng nhập *</Label>
                    <Input placeholder="Nhập tên đăng nhập" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="initialPassword">Mật khẩu ban đầu *</Label>
                    <Input type="password" defaultValue="112233aA" />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreateDialog(false)}
                >
                  Hủy
                </Button>
                <Button 
                  onClick={() => {
                    toast({
                      title: "Thành công",
                      description: "Tạo tài khoản nội bộ thành công!",
                    });
                    setShowCreateDialog(false);
                  }}
                >
                  Tạo tài khoản
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Tìm kiếm theo tên, email, phòng ban..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Lọc theo trạng thái" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả trạng thái</SelectItem>
                <SelectItem value="active">Hoạt động</SelectItem>
                <SelectItem value="locked">Bị khóa</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Danh sách người dùng hệ thống</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[16em]">
                    <div className="flex items-center gap-2">
                      Mã NV
                      <ArrowUpDown className="h-4 w-4 cursor-pointer" />
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[16em]">Thông tin người dùng</TableHead>
                  <TableHead className="min-w-[16em]">Vai trò</TableHead>
                  <TableHead className="min-w-[16em]">Phòng ban</TableHead>
                  <TableHead className="min-w-[16em]">
                    <div className="flex items-center gap-2">
                      Trạng thái
                      <ArrowUpDown className="h-4 w-4 cursor-pointer" />
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[16em]">Lần đăng nhập cuối</TableHead>
                  <TableHead className="w-[200px] text-center sticky right-0 bg-background z-10 border-l">Thao tác</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} className="hover:bg-muted/50">
                    <TableCell 
                      className="font-medium cursor-pointer flex items-center gap-2"
                      onMouseEnter={() => setHoveredField(`id-${user.id}`)}
                      onMouseLeave={() => setHoveredField(null)}
                      onClick={() => copyToClipboard(user.id)}
                    >
                      {user.id}
                      {hoveredField === `id-${user.id}` && (
                        <Copy className="h-3 w-3 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div 
                          className="font-medium cursor-pointer flex items-center gap-2"
                          onMouseEnter={() => setHoveredField(`name-${user.id}`)}
                          onMouseLeave={() => setHoveredField(null)}
                          onClick={() => copyToClipboard(user.name)}
                        >
                          {user.name}
                          {hoveredField === `name-${user.id}` && (
                            <Copy className="h-3 w-3 text-muted-foreground" />
                          )}
                        </div>
                        <div 
                          className="flex items-center gap-1 text-sm text-muted-foreground cursor-pointer"
                          onMouseEnter={() => setHoveredField(`email-${user.id}`)}
                          onMouseLeave={() => setHoveredField(null)}
                          onClick={() => copyToClipboard(user.email)}
                        >
                          <Mail className="h-3 w-3" />
                          {user.email}
                          {hoveredField === `email-${user.id}` && (
                            <Copy className="h-3 w-3 text-muted-foreground ml-1" />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getRoleBadgeColor(user.role)}>
                        {getRoleText(user.role)}
                      </Badge>
                    </TableCell>
                    <TableCell>{user.department}</TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(user.status)}>
                        {user.status === 'active' ? 'Hoạt động' : 'Bị khóa'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {new Date(user.lastLogin).toLocaleString('vi-VN')}
                      </div>
                    </TableCell>
                    <TableCell className="sticky right-0 bg-background z-10 border-l">
                      <div className="flex items-center gap-1 justify-center">
                        <Button size="sm" variant="ghost" title="Xem chi tiết">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Chỉnh sửa">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Khóa/Mở khóa">
                          {user.status === 'active' ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InternalAccounts; 